import { isPastDueDate } from '../../../../shared/utils/overdue';

export function getCompletedVisibleStages(stages = []) {
  return stages.filter((stage) => stage !== 'Due diligence');
}

function parseDealValue(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value || '').replace(/[€\s]/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDateValue(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isDateInCurrentWeek(value) {
  const date = parseDateValue(value);
  if (!date) return false;
  const today = new Date();
  const start = new Date(today);
  const day = start.getDay() || 7;
  start.setDate(start.getDate() - day + 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return date >= start && date <= end;
}

export function getCompletedSettlementStatus(deal = {}) {
  return deal.settlementView?.state === 'resolved'
    ? deal.settlementView.status
    : deal.paymentStatus || deal.settlementStatus;
}

export function isCompletedDealOverdue(deal = {}, now = new Date()) {
  if (getCompletedSettlementStatus(deal) !== 'da_saldare') return false;
  const settlement = deal.settlementView;
  // Accounting dates reflect edited installments and exclude receipts already collected.
  if (settlement?.state === 'resolved' && Array.isArray(settlement.outstandingDueDates)) {
    return settlement.outstandingDueDates.some((date) => isPastDueDate(date, now));
  }
  const installments = deal.installmentDueDates?.length
    ? deal.installmentDueDates : deal.dilazionamento?.scadenze || [];
  const dates = installments.some(Boolean) ? installments
    : [deal.paymentExpectedDate || deal.dataPagamentoPrevista || deal.paymentDate || deal.dataPagamento];
  return dates.some((date) => isPastDueDate(date, now));
}

export function filterCompletedByOverview(deals, overviewFilter) {
  if (!overviewFilter || overviewFilter === 'total') return deals;
  if (overviewFilter === 'thisWeek') {
    return deals.filter((deal) => isDateInCurrentWeek(deal.completedDate));
  }
  if (overviewFilter === 'volume') {
    return deals.filter((deal) => parseDealValue(deal.value) > 0);
  }
  if (overviewFilter === 'overdue') {
    return deals.filter((deal) => isCompletedDealOverdue(deal));
  }
  return deals;
}

export function calculateCompletedStats({
  settledDeals = [],
  dealsToSettle = [],
  dealsWithoutSettlement = [],
  deals = null,
} = {}) {
  const allDeals = deals || [...settledDeals, ...dealsToSettle, ...dealsWithoutSettlement];
  return {
    total: allDeals.length,
    settled: settledDeals.length,
    toSettle: allDeals.filter((deal) => getCompletedSettlementStatus(deal) === 'da_saldare').length,
    overdue: allDeals.filter((deal) => isCompletedDealOverdue(deal)).length,
    unavailable: dealsWithoutSettlement.length,
    thisWeek: allDeals.filter((deal) => isDateInCurrentWeek(deal.completedDate)).length,
    volume: allDeals.reduce((total, deal) => total + parseDealValue(deal.value), 0),
  };
}

export function formatCompletedDealDate(dateString) {
  if (!dateString) return '—';

  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function getCompletedStatusLabel(variant) {
  if (variant === 'toSettle') {
    return 'In attesa di pagamento';
  }

  if (variant === 'unavailable') {
    return 'Settlement non disponibile';
  }

  return 'Saldato';
}

export function getCompletedStatusBadgeClasses(variant) {
  if (variant === 'unavailable') {
    return 'bg-[var(--surface-raised)] text-[var(--text-primary)] border-[var(--border)]';
  }

  return variant === 'toSettle'
    ? 'bg-[var(--warning-soft)] text-[var(--warning-text)] border-[var(--warning-border)]'
    : 'bg-[var(--success-soft)] text-[var(--success-text)] border-[var(--success-border)]';
}

export function getCompletedCardClasses(variant) {
  if (variant === 'unavailable') {
    return {
      cardClass: '',
      hoverBgClass: 'hover:bg-[var(--surface-raised)]',
      draggingBorderClass: 'border-[var(--border)]',
      iconClass: 'text-[var(--text-secondary)]',
    };
  }

  if (variant === 'toSettle') {
    return {
      cardClass: '',
      hoverBgClass: 'hover:bg-[var(--surface-raised)]',
      draggingBorderClass: 'border-[var(--warning-border)]',
      iconClass: 'text-[var(--success-text)]',
    };
  }

  return {
    cardClass: '',
    hoverBgClass: 'hover:bg-[var(--surface-raised)]',
    draggingBorderClass: 'border-[var(--success-border)]',
    iconClass: 'text-[var(--success-text)]',
  };
}
